import React from "react";
import { Briefcase, Users, Home } from "lucide-react";
import PhoneInput from "@/components/PhoneInput";
import DateInput from "@/components/DateInput";

const SPONSOR_REL = ["Father", "Mother", "Spouse", "Sibling", "Relative", "Employer", "Other"];
const HOST_REL = ["Friend", "Relative", "Business contact", "Other"];
const HOST_RES = ["Citizen", "Permanent resident", "Work permit holder"];

/* Reusable add-on form. Caller passes selected add-on ids, current details object,
   and a setter (key -> patch). Sections render only for selected add-ons. */
export default function AddOnDetailsForm({ addOns = [], details = {}, setSection }) {
  return (
    <div className="space-y-6" data-testid="addon-details-form">
      {addOns.includes("noc") && (
        <NocSection details={details.noc || {}} set={(p) => setSection("noc", p)} />
      )}
      {addOns.includes("sponsor_letter") && (
        <SponsorSection details={details.sponsor_letter || {}} set={(p) => setSection("sponsor_letter", p)} />
      )}
      {addOns.includes("invitation_letter") && (
        <InvitationSection details={details.invitation_letter || {}} set={(p) => setSection("invitation_letter", p)} />
      )}
      <style>{`.inp { width: 100%; border: 1px solid #e2e8f0; background: #fff; color: #0f1b2d; border-radius: 6px; padding: 10px 12px; outline: none; font-size: 14px; }
        .inp:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); }`}</style>
    </div>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-lg p-5 sm:p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#2563eb]" />
        <h3 className="font-display font-bold text-base text-[#0f1b2d]">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-[#475569] -mt-2">{subtitle}</p>}
      {children}
    </div>
  );
}
function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#0f1b2d]">
        {label} {required && <span className="text-red-500">*</span>}
        {hint && <span className="ml-2 text-[10px] text-[#64748b] font-normal">{hint}</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
function PhoneIn({ v, on, t }) {
  return <PhoneInput value={v} onChange={on} testId={t} />;
}

function NocSection({ details: d, set }) {
  return (
    <Section icon={Briefcase} title="No Objection Certificate — Employer Details">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Your job title / position" required>
          <input required type="text" data-testid="noc-job-title" value={d.job_title || ""}
            onChange={(e) => set({ job_title: e.target.value })} className="inp" />
        </Field>
        <Field label="Employment start date" required>
          <DateInput value={d.employment_start_date} onChange={(v) => set({ employment_start_date: v })} testId="noc-employment-start" />
        </Field>
        <Field label="Authorized signatory name" required hint="Person who will sign the NOC">
          <input required type="text" data-testid="noc-signatory-name" value={d.signatory_name || ""}
            onChange={(e) => set({ signatory_name: e.target.value })} className="inp" />
        </Field>
        <Field label="Authorized signatory job title" required>
          <input required type="text" data-testid="noc-signatory-title" value={d.signatory_title || ""}
            onChange={(e) => set({ signatory_title: e.target.value })} className="inp" />
        </Field>
        <Field label="Employer name" required hint="Pre-filled from Application Details if available">
          <input required type="text" data-testid="noc-employer-name" value={d.employer_name || ""}
            onChange={(e) => set({ employer_name: e.target.value })} className="inp" />
        </Field>
        <Field label="Employer phone" required>
          <PhoneIn t="noc-employer-phone" v={d.employer_phone} on={(v) => set({ employer_phone: v })} />
        </Field>
      </div>
      <Field label="Employer address" required>
        <textarea required rows={2} data-testid="noc-employer-address" value={d.employer_address || ""}
          onChange={(e) => set({ employer_address: e.target.value })} className="inp" />
      </Field>
    </Section>
  );
}

function SponsorSection({ details: d, set }) {
  return (
    <Section icon={Users} title="Sponsor Letter — Sponsor Information">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Sponsor's full name" required>
          <input required type="text" data-testid="sponsor-name" value={d.sponsor_name || ""}
            onChange={(e) => set({ sponsor_name: e.target.value })} className="inp" />
        </Field>
        <Field label="Relationship to applicant" required>
          <select required data-testid="sponsor-relationship" value={d.relationship || ""}
            onChange={(e) => set({ relationship: e.target.value })} className="inp">
            <option value="">Select…</option>
            {SPONSOR_REL.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Sponsor's occupation / source of income" required>
          <input required type="text" data-testid="sponsor-occupation" value={d.occupation || ""}
            onChange={(e) => set({ occupation: e.target.value })} className="inp" />
        </Field>
        <Field label="Sponsor's phone" required>
          <PhoneIn t="sponsor-phone" v={d.phone} on={(v) => set({ phone: v })} />
        </Field>
      </div>
      <Field label="Sponsor's full address" required>
        <textarea required rows={2} data-testid="sponsor-address" value={d.address || ""}
          onChange={(e) => set({ address: e.target.value })} className="inp" />
      </Field>
      <Field label="Sponsor's email" hint="Optional">
        <input type="email" data-testid="sponsor-email" value={d.email || ""}
          onChange={(e) => set({ email: e.target.value })} className="inp" />
      </Field>
    </Section>
  );
}

function InvitationSection({ details: d, set }) {
  return (
    <Section icon={Home} title="Invitation Letter — Host Information">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Host's full name" required>
          <input required type="text" data-testid="invite-host-name" value={d.host_name || ""}
            onChange={(e) => set({ host_name: e.target.value })} className="inp" />
        </Field>
        <Field label="Host's phone" required>
          <PhoneIn t="invite-host-phone" v={d.host_phone} on={(v) => set({ host_phone: v })} />
        </Field>
        <Field label="Host's email" hint="Optional">
          <input type="email" data-testid="invite-host-email" value={d.host_email || ""}
            onChange={(e) => set({ host_email: e.target.value })} className="inp" />
        </Field>
        <Field label="Relationship to applicant" required>
          <select required data-testid="invite-relationship" value={d.relationship || ""}
            onChange={(e) => set({ relationship: e.target.value })} className="inp">
            <option value="">Select…</option>
            {HOST_REL.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Host's residency status" required>
          <select required data-testid="invite-residency" value={d.residency_status || ""}
            onChange={(e) => set({ residency_status: e.target.value })} className="inp">
            <option value="">Select…</option>
            {HOST_RES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Purpose of visit" required>
          <input required type="text" data-testid="invite-purpose" value={d.purpose || ""}
            onChange={(e) => set({ purpose: e.target.value })} className="inp" />
        </Field>
      </div>
      <Field label="Host's address in Schengen country" required>
        <textarea required rows={2} data-testid="invite-host-address" value={d.host_address || ""}
          onChange={(e) => set({ host_address: e.target.value })} className="inp" />
      </Field>
    </Section>
  );
}
